import { SportType } from '../../models/court/dto/court.dto';
import { EventDto } from '../../models/event/dto/event.dto';
import { EventParticipantDto } from '../../models/event/dto/event-participant.dto';
import { UserDto } from '../../models/user/dto/user.dto';

function escapeCsvCell(value: string | number | undefined | null): string {
    if (value === undefined || value === null) return '';
    const s = String(value);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

function getSportLevelForEvent(user: UserDto, sportType?: SportType) {
    if (!sportType || !user.sportLevels?.length) return null;
    return user.sportLevels.find((sl) => sl.sportType === sportType) ?? null;
}

function participantRow(
    p: EventParticipantDto,
    index: number,
    sportType: SportType | undefined,
    verifiedLabel: string
): string[] {
    const u = p.participant;
    const sportLevel = getSportLevelForEvent(u, sportType);
    const base = [
        index + 1,
        u.firstName,
        u.lastName,
        u.level,
        sportLevel?.identificationNumber ?? '',
        sportLevel?.officialRanking ?? '',
        sportLevel?.isLevelVerified ? verifiedLabel : '',
    ];
    return base.map((v) => escapeCsvCell(v));
}

export type ExportTranslate = (key: string) => string;

/**
 * Builds CSV content for event participants or teams and returns it as a string.
 * For team-based events, each row is a team member with Team # and Role columns.
 * For individual events, each row is a participant with #.
 * Column headers and the "verified" label use the provided translate function.
 */
export function buildEventParticipantsCsv(event: EventDto, translate: ExportTranslate): string {
    const sportType = event.sportType;
    const t = (k: string) => translate(k) || k;
    const verifiedLabel = t('event.export_yes');
    const statusLabel = (status: string) =>
        status === 'complete' ? t('event.team_complete') : t('event.team_incomplete');
    const roleLabel = (role: string) =>
        role === 'leader' ? t('event.team_leader') : t('event.export_role_member');
    const headersParticipant = [
        t('event.export_col_index'),
        t('event.export_col_first_name'),
        t('event.export_col_last_name'),
        t('event.export_col_level'),
        t('event.export_col_license_id'),
        t('event.export_col_ranking'),
        t('event.export_col_license_verified'),
    ];
    const headersTeam = [
        t('event.export_col_team_number'),
        t('event.export_col_team_status'),
        t('event.export_col_slot'),
        t('event.export_col_first_name'),
        t('event.export_col_last_name'),
        t('event.export_col_level'),
        t('event.export_col_role'),
        t('event.export_col_license_id'),
        t('event.export_col_ranking'),
        t('event.export_col_license_verified'),
    ];

    if (event.teamBased && event.teams?.length) {
        const headerRow = headersTeam.map(escapeCsvCell).join(',');
        const rows: string[] = [headerRow];
        event.teams.forEach((team, teamIndex) => {
            team.participants.forEach((p, slotIndex) => {
                const sl = getSportLevelForEvent(p.participant, sportType);
                const ordered = [
                    teamIndex + 1,
                    statusLabel(team.status ?? ''),
                    slotIndex + 1,
                    p.participant.firstName,
                    p.participant.lastName,
                    p.participant.level,
                    roleLabel(p.role ?? ''),
                    sl?.identificationNumber ?? '',
                    sl?.officialRanking ?? '',
                    sl?.isLevelVerified ? verifiedLabel : '',
                ];
                rows.push(ordered.map((v) => escapeCsvCell(v)).join(','));
            });
        });
        return rows.join('\r\n');
    }

    const participants = event.participants ?? [];
    const headerRow = headersParticipant.map(escapeCsvCell).join(',');
    const dataRows = participants.map((p, i) =>
        participantRow(p, i, sportType, verifiedLabel).join(',')
    );
    return [headerRow, ...dataRows].join('\r\n');
}

export function hasParticipantsOrTeamsToExport(event: EventDto): boolean {
    if (event.teamBased) return !!(event.teams?.length && event.teams.some((t) => t.participants?.length));
    return !!(event.participants?.length);
}

/**
 * Triggers a file download of the given CSV content with a suggested filename.
 */
export function downloadCsv(csvContent: string, filename: string): void {
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}
